import Image from "next/image";
import Link from "next/link";
import { Plus } from "lucide-react";
import type { BlogPost, Media } from "@/payload-types";
import { getFocalPosition } from "@/lib/media-utils";
import { siteConfig } from "@/lib/site.config";

interface ArticlesGridCardProps {
  post: BlogPost;
  span?: 1 | 2;
}

export function ArticlesGridCard({ post, span = 1 }: ArticlesGridCardProps) {
  return (
    <div
      className={`decorative-grid-cell grid-cell lg:border-t-0 min-h-48 ${
        span === 2
          ? "col-span-1 aspect-square lg:col-span-2 lg:aspect-[2/1] 2xl:aspect-[8/3]"
          : "col-span-1 aspect-square 2xl:aspect-[4/3]"
      }`}
    >
      <div className="h-full p-3 sm:p-4 lg:p-5">
        <ArticlesGridCardLink post={post} span={span} />
      </div>
    </div>
  );
}

interface ArticlesGridCardLinkProps {
  post: BlogPost;
  span?: 1 | 2;
}

export function ArticlesGridCardLink({ post, span = 1 }: ArticlesGridCardLinkProps) {
  const image =
    post.featuredImage && typeof post.featuredImage === "object"
      ? (post.featuredImage as Media)
      : null;

  const date = post.publishedDate
    ? new Date(post.publishedDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  const href = `${siteConfig.blogPath}/${post.slug}`;

  return (
    <Link href={href} className="group relative block h-full min-h-[200px] overflow-hidden">
      {/* Background image */}
      {image?.url && (
        <Image
          src={image.url}
          alt={image.alt || post.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          style={{ objectPosition: getFocalPosition(image) }}
          sizes={span === 2 ? "(max-width: 1024px) 100vw, 66vw" : "(max-width: 1024px) 100vw, 33vw"}
        />
      )}

      {/* Gradient overlay — left-to-right vignette so text on the left stays legible */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-between h-full p-3 sm:p-4 lg:p-5">
        <div className={span === 2 ? "max-w-[50%]" : ""}>
          <h3 className="font-subheading text-xl lg:text-2xl text-white mb-1 leading-tight">
            {post.title}
          </h3>
          {date && <p className="text-xs text-white/60 mb-2">{date}</p>}
          {post.excerpt && (
            <p className="text-base 2xl:text-lg text-white/80 line-clamp-3">
              {post.excerpt}
            </p>
          )}
        </div>

        <div className="mt-4">
          <span className="inline-flex items-center gap-2 text-sm text-white border border-white/30 px-3 py-1.5 group-hover:border-white/60 transition-colors">
            Read More
            <Plus className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
